import React, { useCallback, useEffect, useRef, useState } from 'react';
import client from '../api/client';
import { notifyCacheRefreshed } from '../utils/cacheEvents';
import './GlobalRefreshBanner.css';

const POLL_MS = 8000;

function formatPhase(status) {
  if (!status) return '';
  if (status.phase === 'facilities') return 'Syncing Salesforce facilities…';
  if (status.currentDataType) {
    const n = status.dataTypesTotal != null && status.dataTypesCompleted != null
      ? ` (${status.dataTypesCompleted + 1} of ${status.dataTypesTotal})`
      : '';
    return `Refreshing ${status.currentDataType}${n}…`;
  }
  return 'Refreshing cached data…';
}

function GlobalRefreshBanner() {
  const [status, setStatus] = useState(null);
  const wasInProgressRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const res = await client.get('/api/cache/refresh-status');
      const next = {
        inProgress: !!res.data?.inProgress,
        triggeredBy: res.data?.triggeredBy,
        startedAt: res.data?.startedAt,
        phase: res.data?.phase,
        currentDataType: res.data?.currentDataType,
        dataTypesCompleted: res.data?.dataTypesCompleted,
        dataTypesTotal: res.data?.dataTypesTotal,
      };

      if (wasInProgressRef.current && !next.inProgress) {
        notifyCacheRefreshed('data');
      }
      wasInProgressRef.current = next.inProgress;
      setStatus(next);
    } catch {
      /* ignore transient errors */
    }
  }, []);

  useEffect(() => {
    poll();
    const ms = status?.inProgress ? 3000 : POLL_MS;
    const id = setInterval(poll, ms);
    return () => clearInterval(id);
  }, [poll, status?.inProgress]);

  if (!status?.inProgress) return null;

  const label = status.triggeredBy === 'scheduled'
    ? 'Scheduled cache refresh in progress'
    : 'Cache refresh in progress';

  return (
    <div className="global-refresh-banner" role="status" aria-live="polite">
      <span className="global-refresh-spinner" aria-hidden="true" />
      <div className="global-refresh-text">
        <strong>{label}</strong>
        <span className="global-refresh-detail">{formatPhase(status)}</span>
      </div>
      <span className="global-refresh-hint">
        Charts and tables may update when this finishes.
      </span>
    </div>
  );
}

export default GlobalRefreshBanner;
